/**
 * Context tab — Collector bindings section (DC-1830).
 *
 * WHAT THIS IS. Which column answers which ARC test, configured by a
 * practitioner instead of seeded by a developer. Until this surface existed the
 * demo rehearsal was the only writer of `use_case_collector_bindings`, so a
 * customer organization could not run a measured assessment on its own data
 * without someone editing server code.
 *
 * THE EDITOR IS NOT HAND-AUTHORED. The template list and every field on the
 * form come from the server's schema-derived descriptors, which are read off
 * each template's own `parameter_schema` — the same schema the run validates
 * against. Registering a seventh interrogation template therefore needs no
 * change here. A discriminated template (`bound_check`, `rank_discrimination`)
 * asks for DIFFERENT fields per branch, so the variant is chosen first and the
 * fields follow it; a flat form would offer fields that cannot coexist.
 *
 * WHAT THIS SURFACE REFUSES TO SHOW. No score, no band, no percentage, no
 * aggregate of any kind. The preview reports the RAW value the template
 * produced and how the population was observed, because what is being checked
 * here is a CONFIGURATION — "does this binding read what I meant it to read" —
 * and not evidence being judged. Judgement belongs to the run, is recorded once
 * as a finding, and is attributable to a catalog version.
 *
 * WHY THERE IS NO EDIT BUTTON. Migration 164 grants SELECT/INSERT/DELETE and
 * REVOKEs UPDATE; the table has no version column and one binding per test.
 * Changing a binding is a removal and a new one — two audited events, because
 * two things happen — and the copy says so rather than leaving the reader to
 * wonder where the pencil went.
 *
 * ARC IP boundary: test codes, control codes and template identifiers only.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { usePermissions } from '../../hooks/useAuth';

// ============================================================================
// The server's shapes (server/banking/assessment/collectorTemplates.ts)
// ============================================================================

type FieldType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'enum'
  | 'string_list'
  | 'object_list'
  | 'object';

type FieldRole = 'dataset' | 'column' | 'column_list' | null;

interface TemplateField {
  name: string;
  type: FieldType;
  required: boolean;
  options: string[] | null;
  defaultValue: string | number | boolean | null;
  role: FieldRole;
  itemFields: TemplateField[] | null;
}

interface TemplateVariant {
  discriminatorValue: string | null;
  fields: TemplateField[];
}

interface TemplateDescriptor {
  metricId: string;
  metricVersion: string;
  dimension: string;
  evidenceLeg: string;
  discriminator: string | null;
  variants: TemplateVariant[];
}

type BindingState = 'bound' | 'unbound' | 'inert_unrouted' | 'inert_other_leg';

interface BindingRow {
  testCode: string;
  controlCode: string;
  leg: string | null;
  state: BindingState;
  binding: {
    id: string;
    collectorId: string;
    metricId: string;
    parameters: Record<string, unknown>;
    createdAt: string;
  } | null;
}

interface DatasetRef {
  journeyId: string;
  journeyName: string;
}

interface PreviewResult {
  testCode: string;
  metricId: string;
  outcome: 'measured' | 'not_assessable';
  value: number | null;
  computedOn: { mode: 'full' | 'sampled'; n?: number; baseline_n?: number; current_n?: number } | null;
  reason: string | null;
  sourceRef: string | null;
}

interface ParameterIssue {
  path: string;
  message: string;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | {
      phase: 'ready';
      templates: TemplateDescriptor[];
      tests: BindingRow[];
      datasets: DatasetRef[];
    };

// ============================================================================
// Copy — every state names its consequence, absence included
// ============================================================================

const STATE_LABEL: Record<BindingState, string> = {
  bound: 'Bound',
  unbound: 'Not bound',
  inert_unrouted: 'Binding present, test unrouted',
  inert_other_leg: 'Binding present, test on another channel',
};

const STATE_CONSEQUENCE: Record<BindingState, string> = {
  bound: 'A run reads this template against the bound dataset.',
  unbound:
    'A run reports “no collector binding” for this test — honestly, and never as a zero.',
  inert_unrouted:
    'The catalog routes this test to no channel, so the run reports “binding present but test unrouted” and this binding never fires.',
  inert_other_leg:
    'The catalog routes this test to another channel, so no collector is consulted and this binding never fires.',
};

// ============================================================================
// Server refusals
// ============================================================================

/**
 * The per-field issues the POST returns, recovered from whichever error shape
 * the transport produced. `useApiClient` attaches the parsed error body to the
 * thrown `ApiError`; a rejection carrying an axios-style `response.data` is
 * read too, so this survives the transport being swapped.
 *
 * Returning `null` — rather than an empty list — distinguishes "the server
 * named no fields" from "the server named these fields", and the caller shows a
 * plain refusal instead of an empty issue list.
 */
function issuesFrom(error: unknown): ParameterIssue[] | null {
  const carriers: unknown[] = [];
  if (typeof error === 'object' && error !== null) {
    const asRecord = error as Record<string, unknown>;
    carriers.push(asRecord.body);
    const response = asRecord.response as Record<string, unknown> | undefined;
    if (response && typeof response === 'object') carriers.push(response.data);
  }
  for (const carrier of carriers) {
    const details = (carrier as { error?: { details?: { issues?: unknown } } } | undefined)?.error
      ?.details?.issues;
    if (Array.isArray(details) && details.length > 0) {
      return details.filter(
        (issue): issue is ParameterIssue =>
          typeof issue === 'object' &&
          issue !== null &&
          typeof (issue as ParameterIssue).message === 'string'
      );
    }
  }
  return null;
}

function messageFrom(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'object' && error !== null) {
    const body = (error as { body?: { error?: { message?: unknown } } }).body;
    if (typeof body?.error?.message === 'string') return body.error.message;
    const data = (error as { response?: { data?: { error?: { message?: unknown } } } }).response
      ?.data;
    if (typeof data?.error?.message === 'string') return data.error.message;
  }
  return fallback;
}

// ============================================================================
// Editor value model
// ============================================================================

type FieldValue = string | string[] | boolean;

function initialValue(field: TemplateField, datasets: DatasetRef[]): FieldValue {
  if (field.role === 'dataset') {
    // Exactly one bound dataset is the overwhelming case and the choice is
    // then not a choice; more than one, and the practitioner must say which.
    return datasets.length === 1 ? datasets[0].journeyId : '';
  }
  if (field.type === 'boolean') return field.defaultValue === true;
  if (field.type === 'string_list') return [];
  if (field.defaultValue !== null) return String(field.defaultValue);
  if (field.type === 'enum' && field.options?.length === 1) return field.options[0];
  return '';
}

function initialValues(fields: TemplateField[], datasets: DatasetRef[]): Record<string, FieldValue> {
  const values: Record<string, FieldValue> = {};
  for (const field of fields) values[field.name] = initialValue(field, datasets);
  return values;
}

/**
 * Assemble the POST body's `parameters` from the form.
 *
 * An empty optional field is OMITTED rather than sent as `''`: a blank box
 * means "I did not say", and sending an empty string would mean "I said
 * nothing", which the template's schema would reject for a different reason
 * than the real one. Structured fields are parsed here so a malformed one is
 * named locally instead of arriving at the server as a string.
 */
function assembleParameters(
  fields: TemplateField[],
  values: Record<string, FieldValue>,
  discriminator: { name: string; value: string } | null
): { outcome: 'ok'; parameters: Record<string, unknown> } | { outcome: 'malformed'; issues: ParameterIssue[] } {
  const parameters: Record<string, unknown> = {};
  const issues: ParameterIssue[] = [];

  if (discriminator) parameters[discriminator.name] = discriminator.value;

  for (const field of fields) {
    const raw = values[field.name];
    if (field.type === 'boolean') {
      parameters[field.name] = raw === true;
      continue;
    }
    if (field.type === 'string_list') {
      const list = Array.isArray(raw) ? raw.filter((entry) => entry !== '') : [];
      if (list.length > 0) parameters[field.name] = list;
      continue;
    }
    const text = typeof raw === 'string' ? raw.trim() : '';
    if (text === '') continue;
    if (field.type === 'number') {
      const parsed = Number(text);
      if (Number.isNaN(parsed)) {
        issues.push({ path: field.name, message: 'This must be a number.' });
        continue;
      }
      parameters[field.name] = parsed;
      continue;
    }
    if (field.type === 'object' || field.type === 'object_list') {
      try {
        parameters[field.name] = JSON.parse(text);
      } catch {
        issues.push({ path: field.name, message: 'This must be valid JSON.' });
      }
      continue;
    }
    parameters[field.name] = text;
  }

  if (issues.length > 0) return { outcome: 'malformed', issues };
  return { outcome: 'ok', parameters };
}

// ============================================================================
// Component
// ============================================================================

export const UseCaseCollectorBindings: React.FC<{ useCaseId: string }> = ({ useCaseId }) => {
  const { get, post, delete: del } = useApiClient();
  const { isAdmin, isAnalyst } = usePermissions();
  const canEdit = isAdmin || isAnalyst;

  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [busy, setBusy] = useState(false);

  // The open editor, or null. One at a time: a binding is per test, and two
  // half-filled forms would invite binding the wrong one.
  const [editorTest, setEditorTest] = useState<string | null>(null);
  const [metricId, setMetricId] = useState('');
  const [variantValue, setVariantValue] = useState('');
  const [values, setValues] = useState<Record<string, FieldValue>>({});
  const [issues, setIssues] = useState<ParameterIssue[] | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const [columns, setColumns] = useState<string[]>([]);
  const [columnsReason, setColumnsReason] = useState<string | null>(null);

  const [preview, setPreview] = useState<{ bindingId: string; result: PreviewResult } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await get<{
        data?: {
          templates?: TemplateDescriptor[];
          tests?: BindingRow[];
          datasets?: DatasetRef[];
        };
      }>(`/api/use-cases/${useCaseId}/collector-bindings`, { skipCache: true });
      setState({
        phase: 'ready',
        templates: res.data?.templates ?? [],
        tests: res.data?.tests ?? [],
        datasets: res.data?.datasets ?? [],
      });
    } catch {
      setState({ phase: 'error' });
    }
  }, [get, useCaseId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Memoized because a fresh `[]` on every render would re-fire every hook
  // that depends on them — including the column fetch, which would then loop.
  const templates = useMemo(
    () => (state.phase === 'ready' ? state.templates : []),
    [state]
  );
  const datasets = useMemo(() => (state.phase === 'ready' ? state.datasets : []), [state]);

  const template = useMemo(
    () => templates.find((candidate) => candidate.metricId === metricId) ?? null,
    [templates, metricId]
  );

  const variant = useMemo(() => {
    if (!template) return null;
    if (template.discriminator === null) return template.variants[0] ?? null;
    return (
      template.variants.find((entry) => entry.discriminatorValue === variantValue) ??
      template.variants[0] ??
      null
    );
  }, [template, variantValue]);

  /** The discriminator is chosen by its own control, so it is not a field. */
  const editableFields = useMemo(() => {
    if (!variant) return [];
    const discriminator = template?.discriminator ?? null;
    return variant.fields.filter((field) => field.name !== discriminator);
  }, [variant, template]);

  const datasetFieldName = useMemo(
    () => editableFields.find((field) => field.role === 'dataset')?.name ?? null,
    [editableFields]
  );
  const chosenJourneyId =
    datasetFieldName !== null && typeof values[datasetFieldName] === 'string'
      ? (values[datasetFieldName] as string)
      : '';

  // The column picker reads the bound dataset's own header. A dataset that
  // cannot be read is NOT a dataset with no columns, and the copy says which.
  useEffect(() => {
    if (editorTest === null || chosenJourneyId === '') {
      setColumns([]);
      setColumnsReason(null);
      return;
    }
    let live = true;
    void (async () => {
      try {
        const res = await get<{
          data?: { status?: string; reason?: string | null; columns?: string[] };
        }>(`/api/use-cases/${useCaseId}/collector-bindings/columns/${chosenJourneyId}`, {
          skipCache: true,
        });
        if (!live) return;
        setColumns(res.data?.columns ?? []);
        setColumnsReason(res.data?.status === 'ok' ? null : res.data?.reason ?? 'unreadable');
      } catch {
        if (!live) return;
        setColumns([]);
        setColumnsReason('unreadable');
      }
    })();
    return () => {
      live = false;
    };
  }, [get, useCaseId, editorTest, chosenJourneyId]);

  const openEditor = useCallback(
    (testCode: string) => {
      const first = templates[0] ?? null;
      const firstVariant = first?.variants[0] ?? null;
      setEditorTest(testCode);
      setMetricId(first?.metricId ?? '');
      setVariantValue(firstVariant?.discriminatorValue ?? '');
      setValues(
        initialValues(
          (firstVariant?.fields ?? []).filter((field) => field.name !== first?.discriminator),
          datasets
        )
      );
      setIssues(null);
      setRefusal(null);
    },
    [templates, datasets]
  );

  const closeEditor = useCallback(() => {
    setEditorTest(null);
    setIssues(null);
    setRefusal(null);
  }, []);

  const chooseTemplate = useCallback(
    (nextMetricId: string) => {
      setMetricId(nextMetricId);
      const next = templates.find((candidate) => candidate.metricId === nextMetricId) ?? null;
      const nextVariant = next?.variants[0] ?? null;
      setVariantValue(nextVariant?.discriminatorValue ?? '');
      setValues(
        initialValues(
          (nextVariant?.fields ?? []).filter((field) => field.name !== next?.discriminator),
          datasets
        )
      );
      setIssues(null);
      setRefusal(null);
    },
    [templates, datasets]
  );

  const chooseVariant = useCallback(
    (nextValue: string) => {
      setVariantValue(nextValue);
      const nextVariant =
        template?.variants.find((entry) => entry.discriminatorValue === nextValue) ?? null;
      setValues(
        initialValues(
          (nextVariant?.fields ?? []).filter((field) => field.name !== template?.discriminator),
          datasets
        )
      );
      setIssues(null);
      setRefusal(null);
    },
    [template, datasets]
  );

  const save = useCallback(async () => {
    if (editorTest === null || template === null) return;
    const assembled = assembleParameters(
      editableFields,
      values,
      template.discriminator === null
        ? null
        : { name: template.discriminator, value: variantValue }
    );
    if (assembled.outcome === 'malformed') {
      setIssues(assembled.issues);
      setRefusal(null);
      return;
    }
    setBusy(true);
    setIssues(null);
    setRefusal(null);
    try {
      await post(`/api/use-cases/${useCaseId}/collector-bindings`, {
        testCode: editorTest,
        metricId: template.metricId,
        parameters: assembled.parameters,
      });
      setEditorTest(null);
      await load();
    } catch (error) {
      const named = issuesFrom(error);
      if (named) setIssues(named);
      else setRefusal(messageFrom(error, 'The binding could not be created.'));
    } finally {
      setBusy(false);
    }
  }, [editorTest, template, editableFields, values, variantValue, post, useCaseId, load]);

  const unbind = useCallback(
    async (bindingId: string) => {
      setBusy(true);
      setRefusal(null);
      try {
        await del(`/api/use-cases/${useCaseId}/collector-bindings/${bindingId}`);
        setPreview(null);
        await load();
      } catch (error) {
        setRefusal(messageFrom(error, 'The binding could not be removed.'));
      } finally {
        setBusy(false);
      }
    },
    [del, useCaseId, load]
  );

  const runPreview = useCallback(
    async (bindingId: string) => {
      setBusy(true);
      setPreview(null);
      setPreviewError(null);
      try {
        const res = await post<{ data?: { preview?: PreviewResult } }>(
          `/api/use-cases/${useCaseId}/collector-bindings/${bindingId}/preview`
        );
        if (res.data?.preview) setPreview({ bindingId, result: res.data.preview });
        else setPreviewError('The binding could not be previewed.');
      } catch (error) {
        setPreviewError(messageFrom(error, 'The binding could not be previewed.'));
      } finally {
        setBusy(false);
      }
    },
    [post, useCaseId]
  );

  // --------------------------------------------------------------------------
  // Field rendering — one control per schema-derived descriptor
  // --------------------------------------------------------------------------

  const setValue = useCallback((name: string, value: FieldValue) => {
    setValues((current) => ({ ...current, [name]: value }));
  }, []);

  function renderField(field: TemplateField): React.ReactNode {
    const testId = `uc-collector-field-${field.name}`;
    const label = (
      <label htmlFor={testId} className="uc-collector-label">
        {field.name}
        {field.required ? '' : ' (optional)'}
      </label>
    );
    const raw = values[field.name];

    if (field.role === 'dataset') {
      return (
        <div className="uc-collector-field" key={field.name}>
          {label}
          <select
            id={testId}
            data-testid={testId}
            value={typeof raw === 'string' ? raw : ''}
            disabled={busy}
            onChange={(e) => setValue(field.name, e.target.value)}
          >
            <option value="">Choose a bound dataset…</option>
            {datasets.map((dataset) => (
              <option key={dataset.journeyId} value={dataset.journeyId}>
                {dataset.journeyName}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (field.role === 'column' || field.role === 'column_list') {
      const multiple = field.role === 'column_list';
      const selected = multiple
        ? Array.isArray(raw)
          ? raw
          : []
        : typeof raw === 'string'
          ? raw
          : '';
      return (
        <div className="uc-collector-field" key={field.name}>
          {label}
          <select
            id={testId}
            data-testid={testId}
            multiple={multiple}
            size={multiple ? Math.min(Math.max(columns.length, 2), 6) : undefined}
            value={selected}
            disabled={busy}
            onChange={(e) => {
              if (!multiple) {
                setValue(field.name, e.target.value);
                return;
              }
              const picked = Array.from(e.target.selectedOptions).map((option) => option.value);
              // jsdom and older engines assign `.value` on a multiple select
              // without repopulating `selectedOptions`; the single assigned
              // value is then the honest reading of what was picked.
              setValue(
                field.name,
                picked.length > 0 ? picked : e.target.value === '' ? [] : [e.target.value]
              );
            }}
          >
            {!multiple && <option value="">Choose a column…</option>}
            {columns.map((column) => (
              <option key={column} value={column}>
                {column}
              </option>
            ))}
          </select>
          {columnsReason !== null && (
            <p className="uc-collector-hint">
              The bound dataset could not be read ({columnsReason}), so its header is unknown. That
              is not a dataset with no columns — the dataset needs attention first.
            </p>
          )}
        </div>
      );
    }

    if (field.type === 'enum' && field.options) {
      return (
        <div className="uc-collector-field" key={field.name}>
          {label}
          <select
            id={testId}
            data-testid={testId}
            value={typeof raw === 'string' ? raw : ''}
            disabled={busy}
            onChange={(e) => setValue(field.name, e.target.value)}
          >
            {!field.required && <option value="">Leave unset</option>}
            {field.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (field.type === 'boolean') {
      return (
        <div className="uc-collector-field" key={field.name}>
          <label htmlFor={testId} className="uc-collector-label">
            <input
              id={testId}
              data-testid={testId}
              type="checkbox"
              checked={raw === true}
              disabled={busy}
              onChange={(e) => setValue(field.name, e.target.checked)}
            />{' '}
            {field.name}
          </label>
        </div>
      );
    }

    if (field.type === 'object' || field.type === 'object_list') {
      return (
        <div className="uc-collector-field" key={field.name}>
          {label}
          <textarea
            id={testId}
            data-testid={testId}
            rows={4}
            value={typeof raw === 'string' ? raw : ''}
            disabled={busy}
            placeholder={
              field.itemFields
                ? `JSON — each entry carries ${field.itemFields.map((f) => f.name).join(', ')}`
                : 'JSON'
            }
            onChange={(e) => setValue(field.name, e.target.value)}
          />
        </div>
      );
    }

    return (
      <div className="uc-collector-field" key={field.name}>
        {label}
        <input
          id={testId}
          data-testid={testId}
          type={field.type === 'number' ? 'number' : 'text'}
          value={typeof raw === 'string' ? raw : ''}
          disabled={busy}
          onChange={(e) => setValue(field.name, e.target.value)}
        />
      </div>
    );
  }

  // --------------------------------------------------------------------------

  const rows = state.phase === 'ready' ? state.tests : [];

  return (
    <div className="uc-collector" data-testid="uc-collector">
      <p className="uc-collector-lede">
        Which interrogation template reads which column to answer which test. A test with no binding
        is a decision still to be made, not an empty row — and changing a binding is a removal and a
        new one, because the store keeps no history of an edit.
      </p>

      {state.phase === 'loading' && (
        <p className="uc-collector-note" aria-busy="true">
          Reading collector bindings…
        </p>
      )}

      {state.phase === 'error' && (
        <p className="uc-collector-note" role="alert" data-testid="uc-collector-error">
          The collector bindings could not be read.
        </p>
      )}

      {state.phase === 'ready' && rows.length === 0 && (
        <p className="uc-collector-note" data-testid="uc-collector-empty">
          No test in this use case’s validated scope is routed to the data channel, and no binding
          exists. Nothing here can be configured until the scope includes a data-channel test.
        </p>
      )}

      {state.phase === 'ready' && rows.length > 0 && (
        <ul className="uc-collector-rows" data-testid="uc-collector-list">
          {rows.map((row) => (
            <li
              key={row.testCode}
              className="uc-collector-row"
              data-testid={`uc-collector-row-${row.testCode}`}
            >
              <div className="uc-collector-row-head">
                <span className="uc-collector-test">{row.testCode}</span>
                <span className="uc-collector-control">{row.controlCode}</span>
                <span
                  className={`uc-collector-state uc-collector-state-${row.state}`}
                  data-testid={`uc-collector-state-${row.testCode}`}
                >
                  {STATE_LABEL[row.state]}
                </span>
              </div>
              <p className="uc-collector-consequence">{STATE_CONSEQUENCE[row.state]}</p>

              {row.binding && (
                <p className="uc-collector-binding">
                  <span className="uc-collector-template-id">{row.binding.metricId}</span>
                  <span className="uc-collector-parameters">
                    {Object.keys(row.binding.parameters).sort().join(', ')}
                  </span>
                </p>
              )}

              {canEdit && row.binding && (
                <div className="uc-collector-row-actions">
                  <button
                    type="button"
                    className="banking-btn banking-btn-secondary"
                    data-testid={`uc-collector-preview-${row.binding.id}`}
                    disabled={busy}
                    onClick={() => void runPreview(row.binding!.id)}
                  >
                    Preview
                  </button>
                  <button
                    type="button"
                    className="banking-btn banking-btn-secondary"
                    data-testid={`uc-collector-unbind-${row.binding.id}`}
                    disabled={busy}
                    onClick={() => void unbind(row.binding!.id)}
                  >
                    Remove binding
                  </button>
                </div>
              )}

              {canEdit && !row.binding && row.state === 'unbound' && (
                <div className="uc-collector-row-actions">
                  <button
                    type="button"
                    className="banking-btn banking-btn-primary"
                    data-testid={`uc-collector-bind-${row.testCode}`}
                    disabled={busy}
                    onClick={() => openEditor(row.testCode)}
                  >
                    Bind a template
                  </button>
                </div>
              )}

              {preview?.bindingId === row.binding?.id && preview && (
                <div className="uc-collector-preview" data-testid="uc-collector-preview-result">
                  {preview.result.outcome === 'measured' ? (
                    <>
                      <span className="uc-collector-preview-value">
                        {String(preview.result.value)}
                      </span>
                      <span className="uc-collector-preview-provenance">
                        {preview.result.computedOn
                          ? `observed on the ${preview.result.computedOn.mode} population${
                              typeof preview.result.computedOn.n === 'number'
                                ? `, n = ${String(preview.result.computedOn.n)}`
                                : ''
                            }${
                              typeof preview.result.computedOn.baseline_n === 'number'
                                ? `, baseline n = ${String(preview.result.computedOn.baseline_n)}`
                                : ''
                            }${
                              typeof preview.result.computedOn.current_n === 'number'
                                ? `, current n = ${String(preview.result.computedOn.current_n)}`
                                : ''
                            }`
                          : 'the template reported no population provenance'}
                      </span>
                      {preview.result.sourceRef && (
                        <span className="uc-collector-preview-source">
                          {preview.result.sourceRef}
                        </span>
                      )}
                      <span className="uc-collector-preview-note">
                        The raw value the template produced. A run judges it; this does not.
                      </span>
                    </>
                  ) : (
                    <span className="uc-collector-preview-refusal">
                      Not assessable — {preview.result.reason ?? 'reason not stated'}
                    </span>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {previewError && (
        <p className="uc-collector-note" role="alert">
          {previewError}
        </p>
      )}

      {editorTest !== null && template !== null && (
        <div className="uc-collector-editor" data-testid="uc-collector-editor">
          <h4>Bind {editorTest}</h4>

          <div className="uc-collector-field">
            <label htmlFor="uc-collector-template" className="uc-collector-label">
              Interrogation template
            </label>
            <select
              id="uc-collector-template"
              data-testid="uc-collector-template"
              value={metricId}
              disabled={busy}
              onChange={(e) => chooseTemplate(e.target.value)}
            >
              <option value="">Choose a template…</option>
              {templates.map((entry) => (
                <option key={entry.metricId} value={entry.metricId}>
                  {entry.metricId}
                </option>
              ))}
            </select>
          </div>

          {template.discriminator !== null && (
            <div className="uc-collector-field">
              <label htmlFor="uc-collector-variant" className="uc-collector-label">
                {template.discriminator}
              </label>
              <select
                id="uc-collector-variant"
                data-testid="uc-collector-variant"
                value={variantValue}
                disabled={busy}
                onChange={(e) => chooseVariant(e.target.value)}
              >
                {template.variants.map((entry) => (
                  <option key={entry.discriminatorValue ?? ''} value={entry.discriminatorValue ?? ''}>
                    {entry.discriminatorValue ?? ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {editableFields.map((field) => renderField(field))}

          {issues && (
            <ul className="uc-collector-issues" data-testid="uc-collector-issues" role="alert">
              {issues.map((issue) => (
                <li key={`${issue.path}:${issue.message}`}>
                  <span className="uc-collector-issue-path">{issue.path}</span> {issue.message}
                </li>
              ))}
            </ul>
          )}

          {refusal && (
            <p className="uc-collector-refusal" role="alert">
              {refusal}
            </p>
          )}

          <div className="uc-collector-editor-actions">
            <button
              type="button"
              className="banking-btn banking-btn-primary"
              data-testid="uc-collector-save"
              disabled={busy}
              onClick={() => void save()}
            >
              Bind
            </button>
            <button
              type="button"
              className="banking-btn banking-btn-secondary"
              data-testid="uc-collector-cancel"
              disabled={busy}
              onClick={closeEditor}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UseCaseCollectorBindings;
